import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type PaginationProps = {
  /** Current active page (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Total records in the dataset */
  totalEntries: number;
  /** Records shown on the current page */
  currentCount: number;
  /** Called when the user clicks a page number or Prev/Next */
  onPageChange: (page: number) => void;
  className?: string;
};

/**
 * Stateless pagination bar.
 * Pair with usePagination() hook for state management.
 *
 * Usage:
 *   <Pagination
 *     page={page}
 *     totalPages={totalPages}
 *     totalEntries={totalEntries}
 *     currentCount={admins.length}
 *     onPageChange={setPage}
 *   />
 */
const Pagination = ({
  page,
  totalPages,
  totalEntries,
  currentCount,
  onPageChange,
  className,
}: PaginationProps) => {
  if (totalPages <= 0) return null;

  // Build page numbers with ellipsis for large sets
  const getPageNumbers = (): (number | "...")[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | "...")[] = [1];
    if (page > 3) pages.push("...");
    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(totalPages - 1, page + 1);
      i++
    ) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between text-sm text-gray-500",
        className
      )}
    >
      {/* Entry count */}
      <span>
        Showing {currentCount} of {totalEntries}{" "}
        {totalEntries === 1 ? "entry" : "entries"}
      </span>

      {/* Page controls */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </Button>

        {getPageNumbers().map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-gray-400">
              …
            </span>
          ) : (
            <Button
              key={p}
              variant={p === page ? "default" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onPageChange(p as number)}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
